import React, { useState, useEffect, useMemo } from 'react';
import { FiTrendingUp, FiUser, FiUsers, FiFileText, FiMessageCircle, FiBook } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { performanceAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import DataTable from '../../components/common/DataTable';

const AdminPerformance = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const { data: response } = await performanceAPI.getAdminManagerPerformance();
        setData(response);
      } catch (error) {
        console.error("Failed to fetch performance data", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const columns = useMemo(() => [
    {
      header: 'Manager',
      accessor: 'managerInfo',
      sortable: true,
      render: (_, row) => (
        <div className="flex items-center">
          <div className="flex-shrink-0 h-10 w-10">
            {row.profileImage ? (
              <img className="h-10 w-10 rounded-full object-cover" src={row.profileImage} alt="" />
            ) : (
              <div className="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                <SafeIcon icon={FiUser} className="h-5 w-5 text-indigo-600" />
              </div>
            )}
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900">{row.fullName}</div>
            <div className="text-sm text-gray-500">{row.agentCode}</div>
          </div>
        </div>
      )
    },
    {
        header: 'Position',
        accessor: 'position',
        render: (val) => <span className="text-gray-500">{val || '-'}</span>
    },
    {
        header: 'Team Size',
        accessor: 'teamSize',
        render: (val) => (
            <div className="flex items-center text-gray-900">
                <SafeIcon icon={FiUsers} className="mr-2 h-4 w-4 text-gray-400" />
                {val}
            </div>
        )
    },
    {
        header: 'Total Sales',
        accessor: 'totalSales',
        render: (val) => (
            <span className="font-medium text-indigo-600">
                {new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 0 }).format(val)}
            </span>
        )
    },
    {
        header: 'Total Policies',
        accessor: 'totalPolicies',
        render: (val) => (
            <div className="flex items-center text-gray-900">
                <SafeIcon icon={FiFileText} className="mr-2 h-4 w-4 text-green-500" />
                {val}
            </div>
        )
    },
    {
        header: 'Coaching',
        accessor: 'totalCoaching',
        render: (val) => (
            <div className="flex items-center text-gray-900">
                <SafeIcon icon={FiMessageCircle} className="mr-2 h-4 w-4 text-orange-500" />
                {val}
            </div>
        )
    },
    {
        header: 'Training',
        accessor: 'totalTraining',
        render: (val) => (
            <div className="flex items-center text-gray-900">
                <SafeIcon icon={FiBook} className="mr-2 h-4 w-4 text-blue-500" />
                {val}
            </div>
        )
    }
  ], []);

  // Calculate summary
  const summary = useMemo(() => {
      return data.reduce((acc, curr) => ({
          totalSales: acc.totalSales + curr.totalSales,
          totalPolicies: acc.totalPolicies + curr.totalPolicies,
          totalCoaching: acc.totalCoaching + curr.totalCoaching,
          totalTraining: acc.totalTraining + curr.totalTraining
      }), { totalSales: 0, totalPolicies: 0, totalCoaching: 0, totalTraining: 0 });
  }, [data]);

  return (
    <div className="px-4 sm:px-6 lg:px-8 min-h-[calc(100vh-100px)] flex flex-col">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Manager Performance</h1>
        <p className="text-gray-600">Overview of team performance and aggregated statistics</p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
         <div className="bg-white rounded-lg shadow p-6 border-l-4 border-indigo-500">
            <div className="flex items-center">
               <div className="p-3 bg-indigo-100 rounded-full">
                  <SafeIcon icon={FiTrendingUp} className="w-6 h-6 text-indigo-600" />
               </div>
               <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Team Sales</p>
                  <p className="text-xl font-bold text-gray-900">
                    {new Intl.NumberFormat('th-TH', { style: 'currency', currency: 'THB', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(summary.totalSales)}
                  </p>
               </div>
            </div>
         </div>
         
         <div className="bg-white rounded-lg shadow p-6 border-l-4 border-green-500">
            <div className="flex items-center">
               <div className="p-3 bg-green-100 rounded-full">
                  <SafeIcon icon={FiFileText} className="w-6 h-6 text-green-600" />
               </div>
               <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Policies</p>
                  <p className="text-xl font-bold text-gray-900">{summary.totalPolicies}</p>
               </div>
            </div>
         </div>

         <div className="bg-white rounded-lg shadow p-6 border-l-4 border-orange-500">
            <div className="flex items-center">
               <div className="p-3 bg-orange-100 rounded-full">
                  <SafeIcon icon={FiMessageCircle} className="w-6 h-6 text-orange-600" />
               </div>
               <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Coaching</p>
                  <p className="text-xl font-bold text-gray-900">{summary.totalCoaching}</p>
               </div>
            </div>
         </div>

         <div className="bg-white rounded-lg shadow p-6 border-l-4 border-blue-500">
            <div className="flex items-center">
               <div className="p-3 bg-blue-100 rounded-full">
                  <SafeIcon icon={FiBook} className="w-6 h-6 text-blue-600" />
               </div>
               <div className="ml-4">
                  <p className="text-sm font-medium text-gray-500">Total Training</p>
                  <p className="text-xl font-bold text-gray-900">{summary.totalTraining}</p>
               </div>
            </div>
         </div>
      </div>

      {loading ? (
        <LoadingSpinner />
      ) : (
        <DataTable 
            columns={columns}
            data={data}
            searchable={true}
            pagination={true}
            pageSize={10}
        />
      )}
    </div>
  );
};

export default AdminPerformance;
